'use client';

import { type CSSProperties, useMemo } from 'react';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { Avatar, AvatarFallback, AvatarImage } from '@/core/ui/primitives/avatar';

type AvatarSize = '4' | '6' | '7' | '8' | '9';

interface AppAvatarProps {
  /**
   * User ID for fetching avatar from /api/avatar/{userId}
   */
  userId?: string | null;

  /**
   * Email for fetching avatar from /api/avatar/by-email?email=
   * Used if userId not provided
   */
  email?: string | null;

  /**
   * Display name for initials fallback
   */
  name?: string | null;

  /**
   * Size variant matching Tailwind h-X w-X classes
   * 4 = 16px, 6 = 24px, 7 = 28px, 8 = 32px, 9 = 36px
   */
  size?: AvatarSize;

  /**
   * Additional className for Avatar wrapper
   */
  className?: string;

  /**
   * Optional inline style for the Avatar wrapper
   * (useful for per-instance border colors, etc.)
   */
  style?: CSSProperties;

  /**
   * Alt text for image (defaults to name)
   */
  alt?: string;
}

const sizeClasses: Record<AvatarSize, string> = {
  '4': 'h-4 w-4 text-[8px]',
  '6': 'h-6 w-6 text-[10px]',
  '7': 'h-7 w-7 text-[10px]',
  '8': 'h-8 w-8 text-xs',
  '9': 'h-9 w-9 text-xs',
};

function getInitials(name?: string | null): string {
  if (!name) return '?';

  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * AppAvatar - Smart avatar component with automatic fallback logic
 *
 * Waterfall logic:
 * 1. Try user's stored avatar from DB (/api/avatar/{userId} or /api/avatar/by-email)
 * 2. Fallback to Gravatar (backend handles this automatically)
 * 3. Fallback to initials from name
 *
 * Usage:
 * ```tsx
 * <AppAvatar userId="123" name="John Doe" size="8" />
 * <AppAvatar email="user@example.com" name="Jane Smith" size="6" />
 * ```
 */
export function AppAvatar({
  userId,
  email,
  name,
  size = '8',
  className,
  style,
  alt,
}: AppAvatarProps) {
  const avatarUrl = useMemo(() => {
    if (userId && typeof userId === 'string') {
      return `/api/avatar/${userId}`;
    }
    if (email && typeof email === 'string') {
      return `/api/avatar/by-email?email=${encodeURIComponent(email)}`;
    }
    return undefined;
  }, [userId, email]);

  const initials = getInitials(name);
  const altText = alt || name || 'User';

  return (
    <Avatar className={cn(sizeClasses[size], className)} style={style}>
      {avatarUrl && (
        <AvatarImage
          src={avatarUrl}
          alt={altText}
          referrerPolicy='no-referrer'
        />
      )}
      <AvatarFallback className={cn('font-semibold', sizeClasses[size])}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

export default AppAvatar;
